import { CommonModule, Location } from '@angular/common';
import { Component, Input, OnInit, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideArrowLeft, lucideLandmark, lucideRefreshCw } from '@ng-icons/lucide';

import type { EntityConfig } from '../../config/entity-config';
import { UI } from '../../components/ui';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { TransactionHubService, type TransactionHubPayload } from '../../services/transaction-hub.service';
import { goBackOrNavigate } from '../../utils/navigation';

type Row = Record<string, unknown>;

/**
 * Provider bank account detail: grouped blocks for the account, plus its statements,
 * transfers and bank pull / push operations (``records/provider-bank-accounts/{id}/hub/``).
 */
@Component({
  selector: 'app-provider-bank-account-detail',
  standalone: true,
  imports: [CommonModule, RouterLink, NgIcon, ...UI],
  templateUrl: './provider-bank-account-detail.component.html',
  viewProviders: [provideIcons({ lucideArrowLeft, lucideLandmark, lucideRefreshCw })],
})
export class ProviderBankAccountDetailComponent implements OnInit {
  @Input() entityConfig!: EntityConfig;
  @Input() id!: string;

  private readonly hubApi = inject(TransactionHubService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  readonly shellRoutes = inject(ShellRoutesService);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly hub = signal<TransactionHubPayload | null>(null);

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.hubApi.getHub(this.entityConfig.listPath, this.id).subscribe({
      next: (data) => {
        this.hub.set(data);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.error?.detail ?? err?.message ?? 'Failed to load bank account.');
        this.loading.set(false);
      },
    });
  }

  goBack(): void {
    goBackOrNavigate(this.location, this.router, this.shellRoutes.commands('manage', this.entityConfig.key));
  }

  account(): Row {
    const a = this.hub()?.['account'];
    return a && typeof a === 'object' ? (a as Row) : {};
  }

  rows(key: 'statements' | 'transfers' | 'pulls' | 'pushes'): Row[] {
    const v = this.hub()?.[key];
    return Array.isArray(v) ? (v as Row[]) : [];
  }

  str(v: unknown): string {
    return v === null || v === undefined || v === '' ? '—' : String(v);
  }

  cell(v: unknown): string {
    if (v && typeof v === 'object') {
      const o = v as Row;
      return [o['primary'] ?? o['top'] ?? o['title'], o['secondary'] ?? o['bottom']]
        .filter((p) => p !== null && p !== undefined && p !== '')
        .join(' · ') || '—';
    }
    return this.str(v);
  }

  statusClass(status: string): string {
    const map: Record<string, string> = {
      ACTIVE: 'bg-success-bg text-success-fg',
      SUCCESS: 'bg-success-bg text-success-fg',
      PENDING: 'bg-warning-bg text-warning-fg',
      PROCESSING: 'bg-info-bg text-info-fg',
      FAILED: 'bg-danger-bg text-danger-fg',
      REJECTED: 'bg-danger-bg text-danger-fg',
    };
    return map[status] ?? 'bg-surface-muted text-fg-secondary';
  }

  statusValue(v: unknown): string {
    if (v && typeof v === 'object') {
      return String((v as Row)['value'] ?? (v as Row)['title'] ?? '');
    }
    return this.str(v);
  }

  metadataJson(): string {
    const m = this.account()['metadata'];
    return m && Object.keys(m as object).length ? JSON.stringify(m, null, 2) : '';
  }

  link(entityKey: string, id: unknown): (string | number | boolean)[] {
    return this.shellRoutes.commands('manage', entityKey, String(id));
  }

  readonly accountFields: { key: string; label: string }[] = [
    { key: 'account_name', label: 'Account name' },
    { key: 'account_number', label: 'Account number' },
    { key: 'customer_number', label: 'Customer number' },
    { key: 'email', label: 'Email' },
    { key: 'currency', label: 'Currency' },
    { key: 'branch_code', label: 'Branch code' },
    { key: 'available_balance', label: 'Available balance' },
    { key: 'ledger_balance', label: 'Ledger balance' },
    { key: 'last_synced_at', label: 'Last synced' },
  ];

  readonly referenceFields: { key: string; label: string }[] = [
    { key: 'merchant_reference', label: 'Merchant reference' },
    { key: 'reference', label: 'Internal reference' },
    { key: 'provider_reference', label: 'Provider reference' },
    { key: 'financial_entity_code', label: 'Provider' },
    { key: 'merchant_name', label: 'Merchant' },
    { key: 'application_name', label: 'Application' },
    { key: 'created_at', label: 'Created' },
    { key: 'updated_at', label: 'Updated' },
  ];
}
